import React, { useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import classNames from 'classnames';
import slugify from 'slugify';

import { recipeMessages } from '@data/ui-messages/recipes';
import {
	CmsRecipeFilterOptions,
	RecipeFilterCriteriaProps,
	RecipeFilterOptionProps,
	RecipesQueryFilters,
} from '@interfaces/common';
import { durationFromMinutes } from '@utils/date';
import { intlRecipeDuration } from '@utils/intl';

import { RecipeFilterCriteria } from './RecipeFilterCriteria';
import styles from './RecipeFilters.module.css';

const getServesOptions = (serves: number[]) => {
	const options: Record<string, RecipeFilterOptionProps> = {};

	for (const serveCount of serves) {
		const name = `serves-${serveCount}`;

		options[name] = {
			name,
			title: serveCount.toString(),
			value: serveCount,
		};
	}

	return Object.values(options);
};

const getTimeOptions = (totalTimes: number[], intl: IntlShape) => {
	const options: Record<string, RecipeFilterOptionProps> = {};

	for (const time of totalTimes) {
		const name = `total-time-${slugify(durationFromMinutes(time))}`;

		options[name] = {
			name,
			title: intlRecipeDuration(intl, time),
			value: time,
		};
	}

	return Object.values(options);
};

const getDifficultyOptions = (intl: IntlShape) => {
	return [
		{
			name: 'easy',
			title: intl.formatMessage(recipeMessages.easy),
			value: 'Easy',
		},
		{
			name: 'medium',
			title: intl.formatMessage(recipeMessages.medium),
			value: 'Medium',
		},
		{
			name: 'hard',
			title: intl.formatMessage(recipeMessages.hard),
			value: 'Hard',
		},
	];
};

const getFilterOptions = (filters: CmsRecipeFilterOptions, intl: IntlShape) => {
	return [
		{
			name: 'serves',
			title: intl.formatMessage(recipeMessages.serves),
			options: getServesOptions(filters.serves),
		},
		{
			name: 'totalTime',
			title: intl.formatMessage(recipeMessages.time),
			options: getTimeOptions(filters.totalTimes, intl),
		},
		{
			name: 'difficulty',
			title: intl.formatMessage(recipeMessages.difficulty),
			options: getDifficultyOptions(intl),
		},
		{
			name: 'categories',
			title: intl.formatMessage(recipeMessages.category),
			options: filters.categories,
		},
		{
			name: 'cuisine',
			title: intl.formatMessage(recipeMessages.cuisine),
			options: filters.cuisines,
		},
	];
};

interface RecipeFiltersProps {
	filterOptions: CmsRecipeFilterOptions;
	align?: 'left' | 'center';
	onFilter?: (filterBy: Partial<RecipesQueryFilters>, isEmpty: boolean) => void;
	className?: string;
}

export const RecipeFilters: React.VFC<RecipeFiltersProps> = ({
	filterOptions,
	onFilter,
	align = 'center',
	className,
}) => {
	const intl = useIntl();
	const filters: RecipeFilterCriteriaProps[] = getFilterOptions(
		filterOptions,
		intl
	);
	const [filterBy, setFilterBy] = useState<Partial<RecipesQueryFilters>>({});
	const styleProperties = {
		'--justify-content': align === 'left' ? 'flex-start' : 'center',
	} as React.CSSProperties;

	const handleChange = (filter: Record<string, any[]>) => {
		let filterCriteriaCount = 0;

		// NOTE: merges and removes empty filter properties
		const updatedFilters = Object.fromEntries(
			Object.entries({ ...filterBy, ...filter }).filter(([, value]) => {
				const isEmpty = value.length === 0;

				if (!isEmpty) {
					filterCriteriaCount += 1;
				}

				return !isEmpty;
			})
		);

		setFilterBy(updatedFilters);

		if (onFilter) {
			onFilter(updatedFilters, filterCriteriaCount === 0);
		}
	};

	return (
		<ul
			aria-label={intl.formatMessage(recipeMessages.recipeFiltersLabel)}
			className={classNames(styles.wrapper, className)}
			data-testid="RecipeFilters"
			style={styleProperties}
		>
			{filters
				.filter((criteria) => criteria.options.length > 1)
				.map((criteria) => (
					<li className={styles.criteria} key={criteria.name}>
						<RecipeFilterCriteria {...criteria} onChange={handleChange} />
					</li>
				))}
		</ul>
	);
};
